/**
 * 75:2's 'Comentários nas publicações' card: the stacked apoiam/neutros/criticam bar and legend
 * over `api`'s `reading/sentiment` route (`AccountSentimentOut`).
 *
 * No existing primitive covers a three-segment proportional bar (`MeterBar` is one segment,
 * `RankRow`/`DimensionBars` are one bar per row); the three segments below are the minimum new
 * markup for that shape, styled after `MeterBar`'s own track/fill tokens.
 */

import type { ReactNode } from 'react';

import { EmptyState } from '../../../../chrome/state/EmptyState';
import { QueryStates } from '../../../../chrome/state/QueryStates';
import { getLocale } from '../../../../i18n';
import { integer } from '../../../../i18n/format';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../ui/card';
import { Skeleton } from '../../../../ui/skeleton';
import { useAccountSentiment, type ReadingWindow } from '../api/queries';
import type { AccountSentiment } from '../api/schemas';
import { am } from '../messages';

type Stance = 'apoiam' | 'neutros' | 'criticam';

const STANCE_ORDER: Stance[] = ['apoiam', 'neutros', 'criticam'];
const STANCE_TONE: Record<Stance, string> = {
  apoiam: 'bg-primary-ink',
  neutros: 'bg-muted-foreground',
  criticam: 'bg-destructive',
};

interface ResolvedSentiment {
  read: number;
  apoiam: number;
  neutros: number;
  criticam: number;
}

/** `collected`, `read`, `apoiam`, `neutros` and `criticam` are null exactly together (`api`'s
 *  `AccountSentimentOut` docstring) -- narrowed explicitly, field by field, rather than defaulting
 *  a missing one to `0` ([null-vs-zero]: a real `0` (comments collected, none read yet -- the
 *  `ACCOUNT_SENTIMENT_UNREAD` fixture) and "nothing entitled" must never render the same bar. */
function resolveSentiment(data: AccountSentiment): ResolvedSentiment | null {
  if (
    data.collected === null ||
    data.read === null ||
    data.apoiam === null ||
    data.neutros === null ||
    data.criticam === null
  ) {
    return null;
  }
  return { read: data.read, apoiam: data.apoiam, neutros: data.neutros, criticam: data.criticam };
}

function isEmptySentiment(data: AccountSentiment): boolean {
  return resolveSentiment(data) === null;
}

function SentimentBody(): ReactNode {
  return (
    <div aria-hidden className="space-y-4">
      <Skeleton className="h-3 w-full rounded-pill" />
      {STANCE_ORDER.map((stance) => (
        <div key={stance} className="flex items-center justify-between gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-10" />
        </div>
      ))}
    </div>
  );
}

export function CommentSentimentSkeleton(): ReactNode {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-4 w-48" />
      </CardHeader>
      <CardContent>
        <SentimentBody />
      </CardContent>
    </Card>
  );
}

function StanceBar(props: { data: ResolvedSentiment }): ReactNode {
  const locale = getLocale();
  const { data } = props;
  const read = data.read;
  const counts: Record<Stance, number> = {
    apoiam: data.apoiam,
    neutros: data.neutros,
    criticam: data.criticam,
  };
  const summary = STANCE_ORDER.map(
    (stance) => `${am(`sentiment.${stance}`)} ${integer(counts[stance], locale)}`,
  ).join(', ');

  return (
    <div className="space-y-4">
      <div
        role="img"
        aria-label={`${am('sentiment.bar')}: ${summary}`}
        className="flex h-track w-full overflow-hidden rounded-pill bg-secondary"
      >
        {STANCE_ORDER.map((stance) => {
          const width = read > 0 ? (counts[stance] / read) * 100 : 0;
          return (
            <div key={stance} className={STANCE_TONE[stance]} style={{ width: `${width}%` }} />
          );
        })}
      </div>
      <ul className="space-y-2">
        {STANCE_ORDER.map((stance) => (
          <li key={stance} className="flex items-center justify-between gap-2 text-body-sm">
            <span className="flex items-center gap-2">
              <span aria-hidden className={`size-2 rounded-full ${STANCE_TONE[stance]}`} />
              {am(`sentiment.${stance}`)}
            </span>
            <span data-numeric>{integer(counts[stance], locale)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CommentSentiment(props: {
  campaignId: string;
  trackedAccountId: string;
  window: ReadingWindow;
}): ReactNode {
  const locale = getLocale();
  const query = useAccountSentiment(props.campaignId, props.trackedAccountId, props.window);
  const resolved = query.data !== undefined ? resolveSentiment(query.data) : null;
  const subtitle =
    resolved !== null && query.data !== undefined && query.data.collected !== null
      ? am('sentiment.subtitle', {
          read: integer(resolved.read, locale),
          collected: integer(query.data.collected, locale),
        })
      : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{am('sentiment.title')}</CardTitle>
        {subtitle !== undefined && <p className="text-body-sm text-muted-foreground">{subtitle}</p>}
      </CardHeader>
      <CardContent>
        <QueryStates
          query={query}
          isEmpty={isEmptySentiment}
          loading={<SentimentBody />}
          empty={
            <EmptyState
              title={am('sentiment.empty.title')}
              description={am('sentiment.empty.description')}
            />
          }
        >
          {(data) => {
            const resolved = resolveSentiment(data);
            return resolved !== null ? (
              <StanceBar data={resolved} />
            ) : (
              <EmptyState
                title={am('sentiment.empty.title')}
                description={am('sentiment.empty.description')}
              />
            );
          }}
        </QueryStates>
      </CardContent>
    </Card>
  );
}
